(() => {
  const SELECTOR = '[data-af-embla]';

  function parseBool(val) {
    if (val == null) return false;
    return String(val).toLowerCase() === 'true';
  }

  function getDatasetStr(el, key, fallback = '') {
    const v = el.dataset[key];
    return v == null || v === '' ? fallback : v;
  }

  function isDesktop() {
    return window.matchMedia('(min-width: 1024px)').matches;
  }

  function createOptions(root) {
    const desktop = isDesktop();

    const align = desktop
      ? getDatasetStr(root, 'alignDesktop', 'start')
      : getDatasetStr(root, 'alignMobile', 'center');

    const containScroll = desktop
      ? getDatasetStr(root, 'containDesktop', 'keepSnaps')
      : getDatasetStr(root, 'containMobile', 'trimSnaps');

    const dragThreshold = Number(getDatasetStr(root, 'dragThreshold', '10')) || 10;
    const slidesToScroll = Number(getDatasetStr(root, 'slidesToScroll', '1')) || 1;
    const dragFree = parseBool(getDatasetStr(root, 'dragFree', 'false'));
    const loop = parseBool(getDatasetStr(root, 'loop', 'false'));
    const inViewThresholdRaw = getDatasetStr(root, 'inViewThreshold', '');
    const inViewThreshold =
      inViewThresholdRaw === '' ? undefined : Math.min(1, Math.max(0, Number(inViewThresholdRaw)));

    const opts = {
      loop,
      align,
      containScroll,
      dragFree,
      slidesToScroll,
      skipSnaps: dragFree,
      watchDrag: true,
      dragThreshold
    };
    if (inViewThreshold != null && !Number.isNaN(inViewThreshold)) {
      opts.inViewThreshold = inViewThreshold;
    }
    return opts;
  }

  function setupWheel(viewportEl, embla, enabled) {
    if (!enabled) return;
    viewportEl.addEventListener(
      'wheel',
      (e) => {
        if (e.deltaY === 0) return;
        e.preventDefault();
        if (e.deltaY > 0) embla.scrollNext();
        else embla.scrollPrev();
      },
      { passive: false }
    );
  }

  function setupArrows(root, embla) {
    const wrapper = root.querySelector('[data-af-embla-arrows]');
    const prevBtn = root.querySelector('[data-af-embla-prev]');
    const nextBtn = root.querySelector('[data-af-embla-next]');

    if (!wrapper || !prevBtn || !nextBtn) return;

    const mode = getDatasetStr(root, 'arrows', 'auto'); // auto | always | never
    if (mode === 'never') {
      wrapper.style.display = 'none';
      return;
    }

    const nextBoundary = getDatasetStr(root, 'arrowsNextBoundary', ''); // '' | 'last-in-view'

    const update = () => {
      const slides = embla.slideNodes();
      const lastIdx = slides.length - 1;
      const inView = embla.slidesInView();

      let canPrev = embla.canScrollPrev();
      let canNext = embla.canScrollNext();

      /* Desktop: when several slides fit the row, canScrollNext() can stay true while the last slide is already visible — gate Next with slidesInView. Mobile peek carousels: the last slide often intersects (peek) before the final snap; same check would disable Next one step early, so only apply on lg+. */
      if (
        nextBoundary === 'last-in-view' &&
        isDesktop() &&
        lastIdx >= 0 &&
        inView.includes(lastIdx)
      ) {
        canNext = false;
      }

      prevBtn.disabled = !canPrev;
      nextBtn.disabled = !canNext;

      if (mode === 'always') {
        wrapper.style.display = '';
        return;
      }

      // auto
      if (!canPrev && !canNext) {
        wrapper.style.display = 'none';
      } else {
        wrapper.style.display = '';
      }
    };

    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      embla.scrollPrev();
    });
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      embla.scrollNext();
    });

    embla.on('select', update);
    embla.on('reInit', update);
    embla.on('slidesInView', update);
    embla.on('scroll', update);
    embla.on('settle', update);
    update();
  }

  function setupOverflowClasses(root, embla) {
    if (!parseBool(getDatasetStr(root, 'overflowClasses', 'false'))) return;
    const update = () => {
      root.classList.toggle('can-scroll-prev', embla.canScrollPrev());
      root.classList.toggle('can-scroll-next', embla.canScrollNext());
    };
    embla.on('scroll', update);
    embla.on('reInit', update);
    embla.on('resize', update);
    update();
  }

  function setupDragCursor(viewport, embla) {
    embla.on('pointerDown', () => viewport.classList.add('is-dragging'));
    embla.on('pointerUp', () => viewport.classList.remove('is-dragging'));
  }

  function teardownEmbla(viewport, embla) {
    if (embla) {
      embla.destroy();
    }
    viewport.classList.remove('is-dragging');
  }

  /** When true, carousel runs only below Tailwind lg (1024px); desktop is static markup + CSS */
  function isOnlyBelowLg(root) {
    return parseBool(getDatasetStr(root, 'afEmblaOnlyBelowLg', 'false'));
  }

  /** When true, Embla runs only if slides overflow the viewport; otherwise static flex (CSS targets data-af-embla-static). */
  function isOnlyIfOverflow(root) {
    return parseBool(getDatasetStr(root, 'afEmblaOnlyIfOverflow', 'false'));
  }

  function trackOverflowsViewport(viewport, container) {
    return container.scrollWidth > viewport.clientWidth + 2;
  }

  function initOne(root) {
    const viewport = root.querySelector('.embla__viewport');
    if (!viewport) return;
    if (!root.querySelector('.embla__slide')) return;

    const Embla = window.EmblaCarousel;
    if (!Embla) return;

    if (isOnlyBelowLg(root)) {
      const mq = window.matchMedia('(max-width: 1023px)');
      let embla = null;

      const syncMobileEmbla = () => {
        if (mq.matches) {
          if (!embla) {
            embla = Embla(viewport, createOptions(root));
            setupArrows(root, embla);
            setupWheel(viewport, embla, parseBool(getDatasetStr(root, 'wheel', 'false')));
            setupOverflowClasses(root, embla);
            setupDragCursor(viewport, embla);
          } else {
            embla.reInit(createOptions(root));
          }
        } else {
          teardownEmbla(viewport, embla);
          embla = null;
        }
      };

      syncMobileEmbla();
      mq.addEventListener('change', syncMobileEmbla);
      window.addEventListener('resize', () => {
        window.setTimeout(syncMobileEmbla, 100);
      });
      return;
    }

    if (isOnlyIfOverflow(root)) {
      let embla = null;
      let resizeTimer;

      const sync = () => {
        const container = root.querySelector('.embla__container');
        if (!container) return;

        const overflow = trackOverflowsViewport(viewport, container);

        if (!overflow) {
          teardownEmbla(viewport, embla);
          embla = null;
          root.setAttribute('data-af-embla-static', 'true');
          return;
        }

        root.removeAttribute('data-af-embla-static');

        if (!embla) {
          embla = Embla(viewport, createOptions(root));
          setupArrows(root, embla);
          setupWheel(viewport, embla, parseBool(getDatasetStr(root, 'wheel', 'false')));
          setupOverflowClasses(root, embla);
          setupDragCursor(viewport, embla);
        } else {
          embla.reInit(createOptions(root));
        }
      };

      const onResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(sync, 100);
      };

      const ro = new ResizeObserver(() => {
        window.requestAnimationFrame(sync);
      });
      ro.observe(viewport);

      window.addEventListener('resize', onResize);

      sync();

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => window.requestAnimationFrame(sync));
      }

      return;
    }

    let embla = Embla(viewport, createOptions(root));

    setupArrows(root, embla);
    setupWheel(viewport, embla, parseBool(getDatasetStr(root, 'wheel', 'false')));
    setupOverflowClasses(root, embla);
    setupDragCursor(viewport, embla);

    window.addEventListener('resize', () => {
      window.setTimeout(() => {
        if (!embla) return;
        embla.reInit(createOptions(root));
      }, 100);
    });
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();

